// Features extras de RRHH (Sprint 3): búsqueda avanzada, ausentismo comparado,
// proyección jubilaciones, plantillas de disposiciones, auditoría del módulo, dashboard ejecutivo.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Chip, CircularProgress,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    IconButton, Tooltip, FormControlLabel, Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';
import TinyEditor from '../../components/TinyEditor';

const PRIMARY = '#0277bd';
const BR = '4px';
const MESES_CORTO = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-AR') : '-';

// ═══════════════ BÚSQUEDA AVANZADA ═══════════════
export function BusquedaAvanzada({ lookups, onSelect, notify }) {
    const [filtros, setFiltros] = useState({
        q: '', idFuncion: '', idArea: '', idCondicion: '',
        edadMin: '', edadMax: '', anosAntMin: '', anosAntMax: '',
        proximoJubilar: false, activo: true,
    });
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    const buscar = async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            Object.entries(filtros).forEach(([k, v]) => {
                if (v === '' || v === null || v === undefined) return;
                if (typeof v === 'boolean') p.append(k, v ? '1' : '0');
                else p.append(k, v);
            });
            const r = await jf(`personal/busqueda-avanzada?${p}`);
            if (r.ok) setItems(r.items || []);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setLoading(false);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>Búsqueda Avanzada de Personal</Typography>

            <Paper sx={{ p: 2, mb: 2, borderRadius: BR, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
                <TextField size="small" label="Buscar (nombre, DNI, CUIL)" value={filtros.q}
                    onChange={e => setFiltros({ ...filtros, q: e.target.value })} />
                <TextField select size="small" label="Función" value={filtros.idFuncion}
                    onChange={e => setFiltros({ ...filtros, idFuncion: e.target.value })}>
                    <MenuItem value="">Todas</MenuItem>
                    {(lookups?.funciones || []).map(f => <MenuItem key={f.Id} value={f.Id}>{f.Funcion}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Área" value={filtros.idArea}
                    onChange={e => setFiltros({ ...filtros, idArea: e.target.value })}>
                    <MenuItem value="">Todas</MenuItem>
                    {(lookups?.areas || []).map(a => <MenuItem key={a.Id} value={a.Id}>{a.Area}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Condición" value={filtros.idCondicion}
                    onChange={e => setFiltros({ ...filtros, idCondicion: e.target.value })}>
                    <MenuItem value="">Todas</MenuItem>
                    {(lookups?.condiciones || []).map(c => <MenuItem key={c.Id} value={c.Id}>{c.Condicion}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Edad mín." value={filtros.edadMin}
                    onChange={e => setFiltros({ ...filtros, edadMin: e.target.value })} />
                <TextField size="small" type="number" label="Edad máx." value={filtros.edadMax}
                    onChange={e => setFiltros({ ...filtros, edadMax: e.target.value })} />
                <TextField size="small" type="number" label="Antig. mín. (años)" value={filtros.anosAntMin}
                    onChange={e => setFiltros({ ...filtros, anosAntMin: e.target.value })} />
                <TextField size="small" type="number" label="Antig. máx. (años)" value={filtros.anosAntMax}
                    onChange={e => setFiltros({ ...filtros, anosAntMax: e.target.value })} />
                <FormControlLabel control={<Switch size="small" checked={filtros.proximoJubilar}
                    onChange={e => setFiltros({ ...filtros, proximoJubilar: e.target.checked })} />}
                    label={<Typography variant="caption">Próximos a jubilarse</Typography>} />
                <FormControlLabel control={<Switch size="small" checked={filtros.activo}
                    onChange={e => setFiltros({ ...filtros, activo: e.target.checked })} />}
                    label={<Typography variant="caption">Solo activos</Typography>} />
                <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#01579b' } }}>
                    Buscar
                </Button>
            </Paper>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box> : (
                <TableContainer component={Paper} sx={{ borderRadius: BR, maxHeight: 'calc(100vh - 320px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead><TableRow>
                            {['Apellido, Nombre', 'CUIL', 'Función', 'Área', 'Condición', 'Edad', 'Antigüedad', ''].map(h =>
                                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#fafafa', fontSize: '0.8rem' }}>{h}</TableCell>
                            )}
                        </TableRow></TableHead>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', color: '#999', py: 3 }}>{filtros.q || filtros.idFuncion ? 'Sin resultados' : 'Usá los filtros y clickeá Buscar'}</TableCell></TableRow>
                            ) : items.map(p => (
                                <TableRow key={p.Id} hover sx={{ cursor: onSelect ? 'pointer' : 'default' }} onClick={() => onSelect?.(p)}>
                                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{p.Apellido}, {p.Nombre}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.CUIL || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Funcion || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Area || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Condicion || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{p.Edad || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{p.AnosAntiguedad || 0} años</TableCell>
                                    <TableCell>{!p.Activo && <Chip label="Inactivo" size="small" sx={{ fontSize: '0.65rem' }} />}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

// ═══════════════ AUSENTISMO COMPARADO ═══════════════
export function AusentismoComparado({ notify }) {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf(`personal/reportes/ausentismo-comparado?ano=${ano}`);
            if (r.ok) setData(r.datos);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setLoading(false);
    }, [ano, notify]);

    useEffect(() => { cargar(); }, [cargar]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;
    if (!data) return null;

    // Armar estructura: mes → { actual, anterior }
    const porMes = {};
    for (const r of data.porMes) {
        if (!porMes[r.Mes]) porMes[r.Mes] = { Mes: r.Mes };
        porMes[r.Mes][r.Periodo] = r;
    }
    const mesesOrdenados = Object.values(porMes).sort((a, b) => a.Mes - b.Mes);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, flex: 1 }}>Ausentismo Comparado</Typography>
                <TextField size="small" type="number" label="Año" value={ano} onChange={e => setAno(parseInt(e.target.value))} sx={{ width: 100 }} />
                <Button variant="contained" onClick={cargar} sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR }}>Ver</Button>
            </Box>

            {/* Tabla comparativa por mes */}
            <Paper sx={{ p: 2, borderRadius: BR, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Comparativa por mes — {ano} vs {ano - 1}</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Mes</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} colSpan={2}>Ausencias</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} colSpan={2}>Tardanzas</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} colSpan={2}>Licencias</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{ano}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center', color: '#999' }}>{ano - 1}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{ano}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center', color: '#999' }}>{ano - 1}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{ano}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center', color: '#999' }}>{ano - 1}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mesesOrdenados.length === 0 ? (
                                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: '#999', py: 3 }}>Sin datos</TableCell></TableRow>
                            ) : mesesOrdenados.map(m => {
                                const delta = (a, b) => b ? ((a - b) / b * 100) : 0;
                                const dAus = delta(m.actual?.Ausencias || 0, m.anterior?.Ausencias || 0);
                                return (
                                    <TableRow key={m.Mes} hover>
                                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{MESES_CORTO[m.Mes]}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', fontWeight: 600 }}>
                                            {m.actual?.Ausencias || 0}
                                            {m.anterior && dAus !== 0 && <Typography variant="caption" sx={{ ml: 0.5, color: dAus > 0 ? '#c62828' : '#2e7d32' }}>({dAus > 0 ? '+' : ''}{dAus.toFixed(0)}%)</Typography>}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#999' }}>{m.anterior?.Ausencias || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', fontWeight: 600 }}>{m.actual?.Tardanzas || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#999' }}>{m.anterior?.Tardanzas || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', fontWeight: 600 }}>{m.actual?.Licencias || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#999' }}>{m.anterior?.Licencias || 0}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Por área */}
            <Paper sx={{ p: 2, borderRadius: BR }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Por área (acumulado {ano})</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead><TableRow>
                            {['Área', 'Personal', 'Ausencias', 'Tardanzas', 'Licencias'].map(h =>
                                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                            )}
                        </TableRow></TableHead>
                        <TableBody>
                            {(data.porArea || []).map((a, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{a.Area}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{a.PersonalEnArea}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#c62828', fontWeight: a.Ausencias > 5 ? 700 : 400 }}>{a.Ausencias}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#e65100' }}>{a.Tardanzas}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#00695c' }}>{a.Licencias}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}

// ═══════════════ PROYECCIÓN JUBILACIONES ═══════════════
export function ProyeccionJubilaciones({ notify }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await jf('personal/reportes/proyeccion-jubilaciones');
                if (r.ok) setItems(r.datos || []);
            } catch (e) { notify?.('Error: ' + e.message, 'error'); }
            setLoading(false);
        })();
    }, [notify]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>Proyección de Jubilaciones</Typography>
            <Alert severity="info" sx={{ mb: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                Fechas estimadas calculadas según edad (F: 60 años, M: 65 años). Ordenado por proximidad.
            </Alert>
            <TableContainer component={Paper} sx={{ borderRadius: BR, maxHeight: 'calc(100vh - 280px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                        {['Apellido, Nombre', 'Función', 'Área', 'Sexo', 'Edad', 'Fecha est. jubilación', 'Días para jubilar'].map(h =>
                            <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>{h}</TableCell>
                        )}
                    </TableRow></TableHead>
                    <TableBody>
                        {items.map(p => {
                            const dias = p.Dias_Para_Jubilar;
                            const color = dias < 0 ? '#c62828' : dias < 365 ? '#e65100' : dias < 365 * 5 ? '#f57f17' : '#666';
                            return (
                                <TableRow key={p.Id} hover>
                                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{p.Apellido}, {p.Nombre}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Funcion || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Area || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.Sexo || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{p.Edad_Actual}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', color, fontWeight: dias < 365 * 2 ? 700 : 400 }}>{fmtDate(p.Fecha_Estimada_Jubilacion)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem', color, fontWeight: 600 }}>
                                        {dias < 0 ? `Ya cumplió (${Math.abs(dias)} días)` : dias < 365 ? `${dias} días` : `${Math.floor(dias / 365)} años ${Math.floor((dias % 365) / 30)} meses`}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

// ═══════════════ ESTADÍSTICAS HISTÓRICAS (multianio) ═══════════════
export function EstadisticasHistoricas({ notify }) {
    const [anos, setAnos] = useState(5);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf(`personal/reportes/estadisticas-historicas?anos=${anos}`);
            if (r.ok) setData(r.datos);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setLoading(false);
    }, [anos, notify]);

    useEffect(() => { cargar(); }, [cargar]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;
    if (!data) return null;

    const asistMap = {};
    for (const r of data.asistencia) asistMap[r.Ano] = r;

    // Licencias por tipo por año
    const tiposLic = Array.from(new Set((data.licencias || []).map(l => l.Tipo)));
    const licMap = {};
    for (const l of data.licencias) {
        licMap[l.Ano] = licMap[l.Ano] || {};
        licMap[l.Ano][l.Tipo] = l;
    }

    const sancMap = {};
    for (const s of data.sanciones) sancMap[s.Ano] = s.Cant;

    const dispMap = {};
    for (const d of data.disposiciones) dispMap[d.Ano] = d.Cant;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, flex: 1 }}>Estadísticas Históricas</Typography>
                <TextField size="small" type="number" label="Años a comparar" value={anos} onChange={e => setAnos(parseInt(e.target.value) || 5)} sx={{ width: 130 }} inputProps={{ min: 2, max: 10 }} />
                <Button variant="contained" onClick={cargar} sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR }}>Ver</Button>
            </Box>

            <Paper sx={{ p: 2, borderRadius: BR, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Asistencia</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Año</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Normales</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Tardanzas</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Min. Tard.</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Ausencias</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Licencias</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {data.anos.map(a => {
                                const row = asistMap[a] || {};
                                return (
                                    <TableRow key={a} hover>
                                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{a}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#2e7d32' }}>{row.DiasNormales || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#e65100', fontWeight: 600 }}>{row.Tardanzas || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{row.TotalMinTardanza || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#c62828', fontWeight: 600 }}>{row.Ausencias || 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', color: '#00695c' }}>{row.DiasLicencia || 0}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: BR, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Licencias aprobadas por tipo</Typography>
                {tiposLic.length === 0 ? <Typography variant="caption" sx={{ color: '#999' }}>Sin datos</Typography> : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Año</TableCell>
                                {tiposLic.map(t => <TableCell key={t} sx={{ fontWeight: 700, textAlign: 'center' }}>{t} (días)</TableCell>)}
                            </TableRow></TableHead>
                            <TableBody>
                                {data.anos.map(a => (
                                    <TableRow key={a} hover>
                                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{a}</TableCell>
                                        {tiposLic.map(t => {
                                            const v = licMap[a]?.[t];
                                            return <TableCell key={t} sx={{ fontSize: '0.82rem', textAlign: 'center' }}>{v ? `${v.Cant} sol. · ${v.TotalDias} días` : '—'}</TableCell>;
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Sanciones por año</Typography>
                    {data.anos.map(a => (
                        <Box key={a} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                            <span>{a}</span>
                            <strong>{sancMap[a] || 0}</strong>
                        </Box>
                    ))}
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>Disposiciones por año</Typography>
                    {data.anos.map(a => (
                        <Box key={a} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                            <span>{a}</span>
                            <strong>{dispMap[a] || 0}</strong>
                        </Box>
                    ))}
                </Paper>
            </Box>
        </Box>
    );
}

// ═══════════════ AUDITORÍA DEL MÓDULO ═══════════════
export function AuditoriaModulo({ notify }) {
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');
    const [usuario, setUsuario] = useState('');
    const [tipo, setTipo] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const buscar = async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (desde) p.append('desde', desde);
            if (hasta) p.append('hasta', hasta);
            if (usuario) p.append('usuario', usuario);
            if (tipo) p.append('tipo', tipo);
            const r = await jf(`personal/auditoria-modulo?${p}`);
            if (r.ok) setItems(r.items || []);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { buscar(); }, []);

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>Auditoría del Módulo</Typography>
            <Paper sx={{ p: 1.5, mb: 2, borderRadius: BR, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={desde} onChange={e => setDesde(e.target.value)} />
                <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={hasta} onChange={e => setHasta(e.target.value)} />
                <TextField size="small" label="Usuario" value={usuario} onChange={e => setUsuario(e.target.value)} sx={{ width: 140 }} />
                <TextField select size="small" label="Fuente" value={tipo} onChange={e => setTipo(e.target.value)} sx={{ width: 140 }}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="licencias">Licencias</MenuItem>
                    <MenuItem value="personal">Personal</MenuItem>
                </TextField>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR }}>Buscar</Button>
                <Typography variant="caption" sx={{ ml: 'auto', color: '#666' }}>{items.length} eventos</Typography>
            </Paper>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box> : (
                <TableContainer component={Paper} sx={{ borderRadius: BR, maxHeight: 'calc(100vh - 300px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead><TableRow>
                            {['Fecha', 'Fuente', 'Persona', 'Acción', 'Estado / Campo', 'Usuario', 'Observación'].map(h =>
                                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#fafafa', fontSize: '0.75rem' }}>{h}</TableCell>
                            )}
                        </TableRow></TableHead>
                        <TableBody>
                            {items.length === 0 ? <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: '#999', py: 3 }}>Sin eventos</TableCell></TableRow>
                            : items.map((e, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{new Date(e.Fecha).toLocaleString('es-AR')}</TableCell>
                                    <TableCell><Chip label={e.Fuente} size="small" sx={{ fontSize: '0.65rem', bgcolor: e.Fuente === 'LICENCIA' ? '#e0f2f1' : '#e3f2fd' }} /></TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{e.Apellido}, {e.Nombre}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{e.Accion}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>
                                        {e.Estado_Anterior ? `${e.Estado_Anterior} → ` : ''}<strong>{e.Estado_Nuevo}</strong>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{e.Usuario || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem', color: '#666', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.Observacion || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

// ═══════════════ PLANTILLAS DE DISPOSICIONES ═══════════════
export function PlantillasDisposiciones({ notify }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editDlg, setEditDlg] = useState({ open: false, data: null });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf('personal/plantillas-disposiciones?soloActivas=0');
            if (r.ok) setItems(r.items || []);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setLoading(false);
    }, [notify]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        try {
            const { data } = editDlg;
            const r = data.Id
                ? await jf(`personal/plantillas-disposiciones/${data.Id}`, { method: 'PUT', body: JSON.stringify({ nombre: data.Nombre, tipo: data.Tipo, contenidoHTML: data.Contenido_HTML, descripcion: data.Descripcion, activa: data.Activa }) })
                : await jf('personal/plantillas-disposiciones', { method: 'POST', body: JSON.stringify({ nombre: data.Nombre, tipo: data.Tipo, contenidoHTML: data.Contenido_HTML, descripcion: data.Descripcion, activa: data.Activa }) });
            if (r.ok) { notify?.('Plantilla guardada', 'success'); setEditDlg({ open: false, data: null }); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    const eliminar = async (id) => {
        if (!window.confirm('¿Eliminar plantilla?')) return;
        try { const r = await jf(`personal/plantillas-disposiciones/${id}`, { method: 'DELETE' }); if (r.ok) { notify?.('Eliminada'); cargar(); } }
        catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, flex: 1 }}>Plantillas de Disposiciones</Typography>
                <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => setEditDlg({ open: true, data: { Nombre: '', Tipo: 'VACACIONES', Contenido_HTML: '', Descripcion: '', Activa: true } })}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR }}>Nueva plantilla</Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                Los placeholders disponibles son: <code>{'{{Apellido}}'}</code>, <code>{'{{Nombre}}'}</code>, <code>{'{{DNI}}'}</code>, <code>{'{{CUIL}}'}</code>, <code>{'{{Funcion}}'}</code>, <code>{'{{Area}}'}</code>, <code>{'{{FechaDesde}}'}</code>, <code>{'{{FechaHasta}}'}</code>, <code>{'{{DiasHabiles}}'}</code>, <code>{'{{Articulo}}'}</code>, <code>{'{{AnoVacaciones}}'}</code>, <code>{'{{NumeroDisposicion}}'}</code>, <code>{'{{FechaHoy}}'}</code>.
            </Alert>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box> : (
                <TableContainer component={Paper} sx={{ borderRadius: BR }}>
                    <Table size="small">
                        <TableHead><TableRow>
                            {['Nombre', 'Tipo', 'Descripción', 'Estado', 'Modificada', ''].map(h =>
                                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>{h}</TableCell>
                            )}
                        </TableRow></TableHead>
                        <TableBody>
                            {items.length === 0 ? <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: '#999', py: 3 }}>Sin plantillas — creá la primera</TableCell></TableRow>
                            : items.map(p => (
                                <TableRow key={p.Id} hover>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{p.Nombre}</TableCell>
                                    <TableCell><Chip label={p.Tipo} size="small" sx={{ fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: '#666', maxWidth: 300 }}>{p.Descripcion || '—'}</TableCell>
                                    <TableCell><Chip label={p.Activa ? 'Activa' : 'Inactiva'} size="small" sx={{ bgcolor: p.Activa ? '#e8f5e9' : '#f5f5f5', color: p.Activa ? '#2e7d32' : '#666', fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{p.Fecha_Modificacion ? fmtDate(p.Fecha_Modificacion) + ` (${p.Modificado_Por || ''})` : '—'}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Editar"><IconButton size="small" onClick={async () => {
                                            const r = await jf(`personal/plantillas-disposiciones/${p.Id}`);
                                            if (r.ok) setEditDlg({ open: true, data: r.plantilla });
                                        }} sx={{ color: PRIMARY }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Eliminar"><IconButton size="small" onClick={() => eliminar(p.Id)} sx={{ color: '#c62828' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={editDlg.open} onClose={() => setEditDlg({ open: false, data: null })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#e3f2fd', color: PRIMARY, fontWeight: 700 }}>
                    {editDlg.data?.Id ? 'Editar plantilla' : 'Nueva plantilla'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {editDlg.data && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField label="Nombre" fullWidth value={editDlg.data.Nombre}
                                onChange={e => setEditDlg({ ...editDlg, data: { ...editDlg.data, Nombre: e.target.value } })} />
                            <TextField select label="Tipo" fullWidth value={editDlg.data.Tipo}
                                onChange={e => setEditDlg({ ...editDlg, data: { ...editDlg.data, Tipo: e.target.value } })}>
                                <MenuItem value="VACACIONES">Vacaciones</MenuItem>
                                <MenuItem value="ART42">Art. 42</MenuItem>
                                <MenuItem value="LICENCIA">Licencia</MenuItem>
                                <MenuItem value="CARTA_MEDICA">Carta médica</MenuItem>
                                <MenuItem value="SANCION">Sanción</MenuItem>
                                <MenuItem value="OTROS">Otros</MenuItem>
                            </TextField>
                            <TextField label="Descripción" fullWidth value={editDlg.data.Descripcion || ''}
                                onChange={e => setEditDlg({ ...editDlg, data: { ...editDlg.data, Descripcion: e.target.value } })} />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                                    Contenido de la plantilla (podés usar placeholders como {'{{Apellido}}'}):
                                </Typography>
                                <TinyEditor
                                    id={`plantilla-editor-${editDlg.data.Id || 'new'}`}
                                    initialHtml={editDlg.data.Contenido_HTML || ''}
                                    onChange={(html) => setEditDlg({ ...editDlg, data: { ...editDlg.data, Contenido_HTML: html } })}
                                />
                            </Box>
                            <FormControlLabel control={<Switch checked={!!editDlg.data.Activa}
                                onChange={e => setEditDlg({ ...editDlg, data: { ...editDlg.data, Activa: e.target.checked } })} />}
                                label="Activa" />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDlg({ open: false, data: null })} sx={{ textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={guardar}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR }}>Guardar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ═══════════════ DASHBOARD EJECUTIVO (PDF impresión) ═══════════════
export function DashboardEjecutivo({ notify }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await jf('personal/dashboard');
                if (r.ok) setData(r);
            } catch (e) { notify?.('Error: ' + e.message, 'error'); }
            setLoading(false);
        })();
    }, [notify]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;
    if (!data) return null;

    const descargarPDF = async () => {
        try {
            const hoy = new Date().toLocaleDateString('es-AR');
            const html = `
                <div style="text-align:center;border-bottom:2px solid #0277bd;padding-bottom:10px;margin-bottom:20px">
                    <div style="font-size:14pt;font-weight:bold;color:#0277bd">FISCALÍA DE ESTADO</div>
                    <div style="font-size:10pt;color:#666">Dashboard Ejecutivo — ${hoy}</div>
                </div>
                <h2 style="color:#0277bd;font-size:13pt">Personal Activo</h2>
                <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa;width:50%"><strong>Internos</strong></td><td style="padding:6px 10px;border:1px solid #ccc">${data.totales?.total_internos || 0}</td></tr>
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa"><strong>Externos</strong></td><td style="padding:6px 10px;border:1px solid #ccc">${data.totales?.total_externos || 0}</td></tr>
                </table>
                <h2 style="color:#0277bd;font-size:13pt">Asistencia hoy</h2>
                <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa;width:50%"><strong>Presentes</strong></td><td style="padding:6px 10px;border:1px solid #ccc;color:#2e7d32">${data.asistenciaHoy?.Normal || 0}</td></tr>
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa"><strong>Tardanzas</strong></td><td style="padding:6px 10px;border:1px solid #ccc;color:#e65100">${data.asistenciaHoy?.Tardanza || 0}</td></tr>
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa"><strong>Ausentes</strong></td><td style="padding:6px 10px;border:1px solid #ccc;color:#c62828">${data.asistenciaHoy?.Ausencia || 0}</td></tr>
                    <tr><td style="padding:6px 10px;border:1px solid #ccc;background:#fafafa"><strong>Con licencia</strong></td><td style="padding:6px 10px;border:1px solid #ccc;color:#00695c">${(data.asistenciaHoy?.Licencia || 0) + (data.asistenciaHoy?.['Art.42'] || 0)}</td></tr>
                </table>
                <h2 style="color:#0277bd;font-size:13pt">Solicitudes Pendientes</h2>
                <p style="margin:0 0 10px"><strong>${(data.solicitudesPendientes || []).length}</strong> solicitudes esperando acción.</p>
                ${(data.solicitudesPendientes || []).length > 0 ? `
                <table style="width:100%;border-collapse:collapse;font-size:9pt">
                    <thead><tr style="background:#f5f5f5"><th style="padding:4px 8px;border:1px solid #ccc">Persona</th><th style="padding:4px 8px;border:1px solid #ccc">Tipo</th><th style="padding:4px 8px;border:1px solid #ccc">Estado</th><th style="padding:4px 8px;border:1px solid #ccc">Desde</th><th style="padding:4px 8px;border:1px solid #ccc">Días</th></tr></thead>
                    <tbody>
                    ${(data.solicitudesPendientes || []).slice(0, 20).map(s =>
                        `<tr><td style="padding:3px 8px;border:1px solid #ccc">${s.Apellido}, ${s.Nombre}</td><td style="padding:3px 8px;border:1px solid #ccc">${s.Tipo}</td><td style="padding:3px 8px;border:1px solid #ccc">${s.Estado}</td><td style="padding:3px 8px;border:1px solid #ccc">${fmtDate(s.Fecha_Desde)}</td><td style="padding:3px 8px;border:1px solid #ccc;text-align:center">${s.Dias_Habiles}</td></tr>`
                    ).join('')}
                    </tbody>
                </table>` : ''}
                ${(data.tardanzasMes || []).length > 0 ? `
                <h2 style="color:#0277bd;font-size:13pt;margin-top:20px">Tardanzas del mes (≥3)</h2>
                <table style="width:100%;border-collapse:collapse;font-size:9pt">
                    <thead><tr style="background:#f5f5f5"><th style="padding:4px 8px;border:1px solid #ccc">Persona</th><th style="padding:4px 8px;border:1px solid #ccc">Función</th><th style="padding:4px 8px;border:1px solid #ccc">Cant.</th><th style="padding:4px 8px;border:1px solid #ccc">Minutos</th></tr></thead>
                    <tbody>
                    ${(data.tardanzasMes || []).slice(0, 15).map(t =>
                        `<tr><td style="padding:3px 8px;border:1px solid #ccc">${t.Apellido}, ${t.Nombre}</td><td style="padding:3px 8px;border:1px solid #ccc">${t.Funcion || '—'}</td><td style="padding:3px 8px;border:1px solid #ccc;text-align:center">${t.CantTardanzas}</td><td style="padding:3px 8px;border:1px solid #ccc;text-align:center">${t.TotalMinutos}</td></tr>`
                    ).join('')}
                    </tbody>
                </table>` : ''}
                <p style="margin-top:30px;color:#999;font-size:9pt;text-align:right">Generado: ${new Date().toLocaleString('es-AR')}</p>
            `;
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const res = await fetch(nodeApiPath('pdf/from-html'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ html, filename: `Dashboard_Ejecutivo_${hoy.replace(/\//g, '-')}`, orientation: 'portrait' }),
            });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Dashboard_Ejecutivo.pdf`; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, flex: 1 }}>Dashboard Ejecutivo</Typography>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={descargarPDF}
                    sx={{ bgcolor: '#c62828', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#b71c1c' } }}>Descargar PDF</Button>
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}
                    sx={{ textTransform: 'none', borderRadius: BR }}>Imprimir</Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2, borderRadius: BR }}>
                Resumen imprimible de indicadores clave. Ideal para llevar a reunión con autoridades.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: PRIMARY, fontWeight: 700, mb: 1 }}>Personal Activo</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{(data.totales?.total_internos || 0) + (data.totales?.total_externos || 0)}</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>{data.totales?.total_internos || 0} internos · {data.totales?.total_externos || 0} externos</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 700, mb: 1 }}>Presentes hoy</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>{data.asistenciaHoy?.Normal || 0}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 700, mb: 1 }}>Tardanzas hoy</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>{data.asistenciaHoy?.Tardanza || 0}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#c62828', fontWeight: 700, mb: 1 }}>Ausentes hoy</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>{data.asistenciaHoy?.Ausencia || 0}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#00695c', fontWeight: 700, mb: 1 }}>Con licencia hoy</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#00695c' }}>{(data.asistenciaHoy?.Licencia || 0) + (data.asistenciaHoy?.['Art.42'] || 0)}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 700, mb: 1 }}>Solicitudes pendientes</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>{(data.solicitudesPendientes || []).length}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#c62828', fontWeight: 700, mb: 1 }}>Tardanzas del mes ≥3</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>{(data.tardanzasMes || []).length}</Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Typography variant="subtitle2" sx={{ color: '#6a1b9a', fontWeight: 700, mb: 1 }}>Cumpleaños semana</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#6a1b9a' }}>{(data.cumples || []).length}</Typography>
                </Paper>
            </Box>
        </Box>
    );
}
